import { motion } from "framer-motion";
import { ArrowUpRight, Search, GitMerge, Hammer, Check } from "lucide-react";

const SITE = "https://www.thebusinessaligned.com";

const SERVICES = [
  { icon: Search, name: "The Review", tag: "See what's there. Find what's missing.", text: "A strategic look at your whole business — brand, offers, systems and presence — to understand what you actually need and what comes next.", href: `${SITE}/alignment-audit` },
  { icon: GitMerge, name: "The Alignment", tag: "Bring the pieces together.", text: "We connect the important parts of your business so your website, offers, message and processes all tell the same clear story.", href: `${SITE}/alignment-reset` },
  { icon: Hammer, name: "The Build", tag: "Make it happen.", text: "Hands-on support to create and implement what your business needs — done with you, not handed to you as a to-do list.", href: `${SITE}/alignment-build` },
];

const METHOD = ["Audit", "Align", "Organize", "Build", "Sustain"];

const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } };

export default function TwoPointOhPage() {
  return (
    <main className="min-h-screen vector-grid pt-32 pb-28" data-testid="two-point-oh-page">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
          <span className="eyebrow">Business Aligned 2.0</span>
          <h1 className="f-display text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4 leading-[1.02]">
            When you want more than a tool — <span className="f-serif text-[#A8785A]">you want a guide.</span>
          </h1>
          <p className="text-[#57473B] text-base sm:text-lg leading-relaxed mt-7" data-testid="two-point-oh-intro">
            The Organizer helps you lay out your business on your own. <strong>Business Aligned 2.0</strong> is the next step for owners who want
            individual, one-on-one guidance on exactly what their business needs — and help building it.
          </p>
          <p className="text-[#8C7562] text-base leading-relaxed mt-4">
            We take a holistic look at the business behind your brand: what you say, what you sell, who it's for, how it feels, how it works, and
            where it shows up. Then we uncover what's disconnected, clarify what matters, organize what's messy, and rebuild what needs to change.
            The goal isn't to add more. It's to make what you already have work better together.
          </p>
          <div className="flex flex-wrap gap-4 mt-9">
            <a href={SITE} target="_blank" rel="noreferrer" className="btn-primary" data-testid="two-point-oh-visit-btn">
              Visit thebusinessaligned.com <ArrowUpRight size={16} />
            </a>
            <a href={`${SITE}/book`} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="two-point-oh-book-btn">
              Book a conversation
            </a>
          </div>
          <p className="f-mono text-[11px] text-[#B9A896] mt-4">OPENS WWW.THEBUSINESSALIGNED.COM IN A NEW TAB</p>
        </motion.div>

        <motion.div {...fadeUp} className="mt-24">
          <span className="eyebrow">Does this sound like you?</span>
          <div className="grid md:grid-cols-3 gap-5 mt-8">
            {[["Your website says one thing.", "Your business says another."], ["Your ideas are everywhere.", "Your processes aren't quite in place."], ["Your vision has grown.", "Your business hasn't fully caught up."]].map(([a, b], i) => (
              <div key={i} className="rounded-2xl border border-[#E8DCCB] bg-[#FFFDF7] p-8" data-testid={`two-point-oh-pain-${i}`}>
                <span className="f-mono text-[#A8785A] text-xs block mb-5">0{i + 1}</span>
                <p className="f-display text-2xl tracking-tight leading-tight">{a}</p>
                <p className="f-serif text-[#A8785A] text-lg mt-2">{b}</p>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div {...fadeUp} className="mt-24">
          <span className="eyebrow">Three ways to work together</span>
          <h2 className="f-display text-3xl sm:text-4xl tracking-tight mt-4">Start where you are.</h2>
          <div className="grid md:grid-cols-3 gap-5 mt-10">
            {SERVICES.map((s) => (
              <a key={s.name} href={s.href} target="_blank" rel="noreferrer" className="group glass rounded-2xl p-8 flex flex-col hover:border-[#D4A0B4] transition-colors" data-testid={`two-point-oh-service-${s.name.toLowerCase().replace(/\s+/g, "-")}`}>
                <span className="w-11 h-11 rounded-xl bg-[#D4A0B4]/15 border border-[#D4A0B4]/40 flex items-center justify-center mb-6">
                  <s.icon size={18} className="text-[#D4A0B4]" />
                </span>
                <h3 className="f-display text-2xl tracking-tight">{s.name}</h3>
                <p className="f-serif text-[#A8785A] mt-1">{s.tag}</p>
                <p className="text-sm text-[#8C7562] leading-relaxed mt-4 flex-1">{s.text}</p>
                <p className="f-mono text-[10px] text-[#B9A896] mt-6">CUSTOM INVESTMENT BASED ON SCOPE</p>
                <span className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-[#A8785A] group-hover:gap-3 transition-[gap]">Learn more <ArrowUpRight size={15} /></span>
              </a>
            ))}
          </div>
        </motion.div>

        <motion.div {...fadeUp} className="mt-24 grid lg:grid-cols-[1fr_1.2fr] gap-12 items-start">
          <div>
            <span className="eyebrow">The method</span>
            <h2 className="f-display text-3xl sm:text-4xl tracking-tight mt-4 leading-[1.08]">The Business Alignment Method</h2>
            <p className="text-sm text-[#8C7562] leading-relaxed mt-5 max-w-md">
              Every engagement follows the same five moves, so you always know where you are and what comes next.
            </p>
          </div>
          <div className="flex flex-col divide-y divide-[#E8DCCB] rounded-2xl border border-[#E8DCCB] bg-[#FFFDF7]">
            {METHOD.map((m, i) => (
              <div key={m} className="flex items-center gap-5 px-7 py-5" data-testid={`two-point-oh-method-${i}`}>
                <span className="f-mono text-[#A8785A] text-xs w-6">0{i + 1}</span>
                <span className="f-display text-xl tracking-tight flex-1">{m}</span>
                <Check size={15} className="text-[#D4A0B4]" />
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div {...fadeUp} className="mt-24 glass rounded-2xl p-8 sm:p-12 flex flex-col md:flex-row md:items-center gap-8">
          <div className="flex-1">
            <span className="eyebrow">Not sure where you fit?</span>
            <h2 className="f-display text-3xl tracking-tight mt-3">That's okay. Start with a conversation.</h2>
            <p className="text-sm text-[#8C7562] leading-relaxed mt-3 max-w-xl">
              You don't have to figure it out before we talk. Tell us about your business, your goals and where you are right now — we'll work out what you need from there.
            </p>
          </div>
          <a href={`${SITE}/book`} target="_blank" rel="noreferrer" className="btn-primary shrink-0" data-testid="two-point-oh-cta-btn">
            Book my call <ArrowUpRight size={16} />
          </a>
        </motion.div>
      </div>
    </main>
  );
}
